<template>
  <f7-block
    v-if="visible"
    class="campaign-setup-checklist"
    strong
    inset
  >
    <!-- Readiness & Lifecycle Hero Card -->
    <div class="setup-hero bg-surface-pure rounded-xl p-4 shadow-avocash-sm display-flex flex-direction-column mb-3">
      <div class="display-flex align-items-center justify-content-space-between">
        <div class="display-flex align-items-center gap-2">
          <div class="w-8 h-8 rounded-full bg-primary-fixed display-flex align-items-center justify-content-center text-on-primary-fixed">
            <f7-icon material="rocket_launch" :size="20" />
          </div>
          <div>
            <h2 class="font-label-lg text-label-lg text-on-surface">{{ $t("campaignSetup.title") }}</h2>
            <p class="font-label-sm text-label-sm text-on-surface-variant">{{ progressLabel }}</p>
          </div>
        </div>
        <span class="font-headline-sm text-primary">{{ progressPct }}%</span>
      </div>

      <div class="display-flex flex-direction-column gap-1.5">
        <div class="setup-segments">
          <span
            v-for="n in totalSegmentCount"
            :key="n"
            class="setup-segment"
            :class="segmentClass(n)"
          />
        </div>
        <div class="stage-legend display-flex align-items-center justify-content-space-between font-label-sm text-on-surface-variant overflow-x-auto no-scrollbar">
          <span class="text-primary font-semibold display-flex align-items-center gap-0.5">
            <f7-icon material="description" :size="12" />
            {{ $t("campaignSetup.stageDraft") }}
          </span>
          <span class="text-primary font-semibold display-flex align-items-center gap-0.5">
            <f7-icon material="sentiment_satisfied" :size="12" />
            {{ $t("campaignSetup.stageSocial") }}
          </span>
          <span class="text-secondary font-semibold display-flex align-items-center gap-0.5">
            <f7-icon material="account_balance" :size="12" />
            {{ $t("campaignSetup.stageFinance") }}
          </span>
          <span class="text-outline">{{ $t("campaignSetup.stageKyc") }}</span>
          <span class="text-outline">{{ $t("campaignSetup.stagePay") }}</span>
        </div>
      </div>

      <div class="bg-surface-container-low p-3 rounded-lg display-flex align-items-flex-start gap-2.5">
        <span class="material-symbols-outlined text-primary flex-shrink-0 mt-0.5" style="font-size: 20px">shield_with_heart</span>
        <p class="font-body-sm text-body-sm text-on-surface-variant leading-snug">
          {{ $t("campaignSetup.infoMicrocopy") }}
        </p>
      </div>
    </div>

    <!-- Checklist Tasks Header -->
    <div class="display-flex align-items-center justify-content-space-between px-1 pt-3 pb-2">
      <div class="display-flex align-items-center gap-1.5">
        <h3 class="font-headline-sm text-on-surface">{{ $t("campaignSetup.roadmap") }}</h3>
        <span class="w-2 h-2 rounded-full bg-secondary-fixed-dim"></span>
      </div>
      <span class="font-label-md text-primary">{{ $t("campaignSetup.actionsRequired", { n: pendingCount }) }}</span>
    </div>

    <div class="setup-items">
      <article
        v-for="item in items"
        :key="item.key"
        class="setup-item-card bg-surface-pure rounded-xl p-3.5 shadow-avocash-sm display-flex flex-direction-column relative overflow-hidden"
        :class="{ done: item.done, 'setup-item-blocking': item.blocking }"
        role="button"
        tabindex="0"
        @click="runItem(item)"
        @keydown.enter="runItem(item)"
        @keydown.space.prevent="runItem(item)"
      >
        <div v-if="item.blocking && !item.done" class="setup-blocking-bar"></div>
        <div class="display-flex align-items-flex-start justify-content-space-between gap-2">
          <div class="display-flex align-items-center gap-2.5 pl-1">
            <div
              class="w-8 h-8 rounded-full display-flex align-items-center justify-content-center flex-shrink-0"
              :class="itemIconClass(item)"
            >
              <f7-icon :material="itemIcon(item)" :size="20" />
            </div>
            <div>
              <h4 class="font-label-lg text-label-lg text-on-surface">{{ item.title }}</h4>
              <p class="font-label-sm text-label-sm font-medium" :class="itemSubClass(item)">{{ item.done ? $t('campaignSetup.doneLabel') : item.tagLine }}</p>
            </div>
          </div>
          <span
            class="px-2.5 py-1 rounded-full font-label-sm uppercase tracking-wide whitespace-nowrap"
            :class="itemBadgeClass(item)"
          >
            {{ itemBadge(item) }}
          </span>
        </div>
        <p class="font-body-sm text-body-sm text-on-surface-variant pl-1">{{ item.desc }}</p>
        <div class="pl-1 pt-0.5 display-flex align-items-center justify-content-space-between gap-2 flex-wrap">
          <span v-if="!item.done" class="font-label-sm text-on-surface-variant display-flex align-items-center gap-1">
            <f7-icon material="timer" :size="16" class="text-secondary" />
            {{ $t('campaignSetup.timeShort') }}
          </span>
          <f7-button
            round
            raised
            fill
            class="h-10 px-4 rounded-lg font-label-lg shadow-avocash-sm transition-all display-flex align-items-center gap-1.5 setup-cta cursor-pointer"
            :class="itemCtaClass(item)"
            type="button"
          >
            {{ item.done ? $t('campaignSetup.done') : item.cta }}
            <f7-icon :material="item.done ? 'check' : 'arrow_forward'" :size="18" />
          </f7-button>
        </div>
      </article>
    </div>

    <div class="setup-footer pt-2 justify-content-flex-end display-flex">
      <f7-link small @click="dismiss">
        <f7-icon f7="xmark" size="12" /> {{ $t("campaignSetup.dismiss") }}
      </f7-link>
    </div>
  </f7-block>
</template>

<script setup>
import { computed, ref } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  groupId: { type: String, required: true },
  campaign: { type: Object, default: null },
  canManage: { type: Boolean, default: false },
  membersCount: { type: Number, default: 1 },
})
const emit = defineEmits(['banner', 'logo', 'launch'])

const DISMISS_KEY = 'avocash:campaign-setup-checklist:dismissed:'
const copied = ref(false)

const isDraft = computed(() => props.campaign?.status === 'DRAFT')
const kycRoute = computed(() => `/kyc/individual/${props.groupId}`)

const items = computed(() => {
  const c = props.campaign
  if (!c) return []
  const list = []
  if (isDraft.value) {
    list.push({
      key: 'launch',
      icon: 'rocket_launch',
      title: t('campaignSetup.launch'),
      desc: t('campaignSetup.launchDesc'),
      done: false,
      action: 'launch',
      tagLine: t('campaignSetup.launchTag'),
      cta: t('campaignSetup.launchButton'),
      status: 'action',
    })
  }
  list.push(
    {
      key: 'banner',
      icon: 'photo',
      title: t('campaignSetup.addBanner'),
      desc: t('campaignSetup.addBannerDesc'),
      done: !!c.banner,
      action: 'banner',
      tagLine: t('campaignSetup.brandTag'),
      cta: t('campaignSetup.addButton'),
      status: 'optional',
    },
    {
      key: 'logo',
      icon: 'photo_camera',
      title: t('campaignSetup.addLogo'),
      desc: t('campaignSetup.addLogoDesc'),
      done: !!c.logo,
      action: 'logo',
      tagLine: t('campaignSetup.brandTag'),
      cta: t('campaignSetup.addButton'),
      status: 'optional',
    },
    {
      key: 'invite',
      icon: 'person_add',
      title: t('campaignSetup.invite'),
      desc: t('campaignSetup.inviteDesc'),
      done: props.membersCount >= 3,
      href: `/groups/${props.groupId}/members/import`,
      tagLine: t('campaignSetup.foundersTag'),
      cta: t('campaignSetup.inviteButton'),
      status: 'done-or-action',
    },
    {
      key: 'recipient',
      icon: 'verified_user',
      title: t('campaignSetup.verifyRecipient'),
      desc: t('campaignSetup.verifyRecipientDesc'),
      done:
        !!c.payoutSetup?.verified ||
        c.payoutSetup?.recipientVerified === true ||
        c.recipientVerified === true,
      href: kycRoute.value,
      tagLine: t('campaignSetup.recipientTag'),
      cta: t('campaignSetup.verifyButton'),
      status: 'blocking',
      blocking: true,
    },
    {
      key: 'share',
      icon: 'share',
      title: t('campaignSetup.share'),
      desc: t('campaignSetup.shareDesc'),
      done: copied.value,
      action: 'share',
      tagLine: t('campaignSetup.shareTag'),
      cta: t('campaignSetup.shareButton'),
      status: 'action',
    },
  )
  return list
})

const doneCount = computed(() => items.value.filter((i) => i.done).length)
const pendingCount = computed(() => items.value.filter((i) => !i.done).length)
const total = computed(() => items.value.length)
const progressPct = computed(() =>
  total.value ? Math.round((doneCount.value / total.value) * 100) : 0,
)
const totalSegmentCount = computed(() => 5)
function segmentClass(n) {
  if (n < 3) return 'bg-primary'
  if (n === 3) return 'setup-segment--current animate-pulse'
  return 'bg-surface-container-highest'
}
const progressLabel = computed(() =>
  t('campaignSetup.subtitle', { done: doneCount.value, total: total.value }),
)

function itemIcon(item) {
  if (item.done) return 'done_all'
  return item.icon
}
function itemIconClass(item) {
  if (item.done) return 'bg-primary-fixed text-primary'
  if (item.status === 'blocking') return 'bg-error-container text-error'
  if (item.status === 'optional') return 'bg-surface-container text-on-surface-variant'
  return 'setup-avatar-action text-primary'
}
function itemSubClass(item) {
  if (item.done) return 'text-primary'
  if (item.status === 'blocking') return 'text-error'
  if (item.status === 'optional') return 'text-on-surface-variant'
  return 'text-secondary'
}
function itemBadge(item) {
  if (item.done) return t('campaignSetup.statusDone')
  const map = {
    blocking: 'campaignSetup.statusBlocking',
    optional: 'campaignSetup.statusOptional',
    action: 'campaignSetup.statusRequired',
    'done-or-action': 'campaignSetup.statusRequired',
  }
  return t(map[item.status] || 'campaignSetup.statusRequired')
}
function itemBadgeClass(item) {
  if (item.done) return 'bg-primary-fixed text-on-primary-fixed'
  if (item.status === 'blocking') return 'bg-error-container text-on-error-container font-semibold'
  if (item.status === 'optional') return 'bg-surface-container-low text-on-surface-variant'
  return 'bg-secondary-container text-on-secondary-container'
}
function itemCtaClass(item) {
  if (item.done) return 'bg-surface-container text-on-surface-variant'
  if (item.status === 'blocking') return 'bg-surface-container text-on-surface'
  if (item.status === 'optional') return 'bg-surface-container text-on-surface-variant'
  return 'bg-primary text-on-primary'
}

const dismissed = ref(false)
function isDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY + props.campaign?.id) === '1'
  } catch (e) {
    return false
  }
}
function dismiss() {
  dismissed.value = true
  try {
    localStorage.setItem(DISMISS_KEY + props.campaign?.id, '1')
  } catch (e) {
    // ignore
  }
}

const visible = computed(
  () =>
    props.canManage &&
    !dismissed.value &&
    !isDismissed() &&
    items.value.length > 0 &&
    doneCount.value < total.value,
)

async function runItem(item) {
  if (item.done) return
  if (item.action === 'banner') return emit('banner')
  if (item.action === 'logo') return emit('logo')
  if (item.action === 'launch') return emit('launch')
  if (item.action === 'share') return shareCampaign()
  if (item.href) f7.router.navigate(item.href)
}

async function shareCampaign() {
  const c = props.campaign
  const url = `https://avocash.com/groups/${props.groupId}/campaigns/${c.id}`
  const text = t('campaignSetup.shareText', { name: c.name })
  if (navigator.share) {
    try {
      await navigator.share({ title: c.name, text, url })
      copied.value = true
    } catch (e) {
      // user cancelled
    }
    return
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${text} ${url}`)
    copied.value = true
    f7.toast.show({ text: t('campaignSetup.copied'), closeTimeout: 1500 })
  }
}
</script>

<style scoped>
.campaign-setup-checklist {
  background: transparent;
  border: none;
}
.setup-segments {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  height: 8px;
  width: 100%;
}
.setup-segment {
  border-radius: 9999px;
  height: 100%;
}
.stage-legend {
  font-size: 10px;
  padding-top: 2px;
}
.setup-cta {
  margin: 0;
}
.setup-footer :deep(.link) {
  font-size: 12px;
}

/* Theme-aware additions (replacing missing Tailwind-style utilities) */
.setup-hero { gap: 14px; }
.setup-item-card {
  gap: 10px;
  cursor: pointer;
  transition: transform 0.1s ease;
}
.setup-item-card:active { transform: scale(0.99); }
.setup-blocking-bar {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 6px;
  background: var(--f7-theme-color, #1a6b42);
}
.setup-avatar-action {
  background: color-mix(in srgb, var(--f7-theme-color) 12%, var(--f7-card-bg-color, #fff));
}
.setup-segment--current {
  background: var(--f7-secondary, #fed977);
}
</style>